import { motion } from "framer-motion";
import moment from "moment";
import { useEffect, useState } from "react";
import { FaBoxOpen, FaChevronRight } from "react-icons/fa6";
import SummaryApi from "../common";
import OrderDetailsModal from "../components/OrderDetailsModal";
import RefundRequestModal from "../components/RefundRequestModal";
import {
  ORDER_DATE_LABEL,
  ORDER_DETAILS_LINK,
  ORDER_EMPTY_SUBTITLE,
  ORDER_EMPTY_TITLE,
  ORDER_ID_LABEL,
  ORDER_PAGE_SUBTITLE,
  ORDER_PAGE_TITLE,
  ORDER_QTY_LABEL,
  ORDER_RETURN_ITEM,
  ORDER_START_SHOPPING,
  ORDER_STATUS_PAID,
  ORDER_STATUS_PROCESSING,
  ORDER_TOTAL_LABEL,
  ROUTES,
} from "../constants";
import displayNPRCurrency from "../helpers/displayCurrency";
import { request } from "../helpers/request";

const OrderPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentOrderDetails, setCurrentOrderDetails] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const handleDetailsClick = (order) => {
    setCurrentOrderDetails(order);
    setIsDetailsModalOpen(true);
  };

  const handleReturnClick = (orderId, product) => {
    setSelectedOrder(orderId);
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await request(SummaryApi.getOrder);
      const responseData = await response.json();
      setData(responseData.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, []);

  if (loading) {
    return (
      <div className="bg-surface-muted min-h-screen py-8 md:py-12 pb-28 lg:pb-12">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-surface-border overflow-hidden"
            >
              <div className="h-16 shimmer" />
              <div className="p-5 space-y-4">
                {[1, 2].map((j) => (
                  <div key={j} className="flex gap-4">
                    <div className="w-16 h-16 rounded-xl shimmer flex-shrink-0" />
                    <div className="flex-1 space-y-2 py-1">
                      <div className="h-4 shimmer rounded-full w-3/4" />
                      <div className="h-3 shimmer rounded-full w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-muted min-h-screen py-8 md:py-12 pb-28 lg:pb-12">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        {/* Page Header */}
        <div className="mb-6 md:mb-10">
          <h1 className="text-2xl md:text-3xl font-black text-primary tracking-tight">
            {ORDER_PAGE_TITLE}
          </h1>
          <p className="text-sm text-neutral-500 font-medium mt-1">
            {ORDER_PAGE_SUBTITLE}
          </p>
        </div>

        {data.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl border border-surface-border shadow-card p-10 md:p-16 text-center max-w-md mx-auto mt-8"
          >
            <div className="w-16 h-16 md:w-20 md:h-20 bg-surface-muted rounded-full flex items-center justify-center mx-auto mb-5">
              <FaBoxOpen className="text-neutral-300 text-2xl md:text-3xl" />
            </div>
            <h2 className="text-lg md:text-xl font-black text-primary mb-2">
              {ORDER_EMPTY_TITLE}
            </h2>
            <p className="text-sm text-neutral-500 mb-8 leading-relaxed">
              {ORDER_EMPTY_SUBTITLE}
            </p>
            <a href={ROUTES.HOME} className="btn-primary inline-flex">
              {ORDER_START_SHOPPING}
            </a>
          </motion.div>
        ) : (
          <div className="space-y-4 md:space-y-5">
            {data.map((item, index) => {
              const isPaid = item.paymentDetails?.payment_status === "paid";
              return (
                <motion.div
                  key={item._id + index}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.06 }}
                  className="bg-white rounded-2xl border border-surface-border shadow-card overflow-hidden"
                >
                  {/* Order Header */}
                  <div className="px-4 md:px-6 py-3 md:py-4 bg-surface-muted/50 border-b border-surface-border flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
                    <div className="flex items-center gap-4 md:gap-8">
                      <div>
                        <p className="tag text-neutral-400 mb-0.5">
                          {ORDER_DATE_LABEL}
                        </p>
                        <p className="text-xs md:text-sm font-bold text-primary">
                          {moment(item.createdAt).format("MMM D, YYYY")}
                        </p>
                      </div>
                      <div>
                        <p className="tag text-neutral-400 mb-0.5">
                          {ORDER_TOTAL_LABEL}
                        </p>
                        <p className="text-xs md:text-sm font-black text-accent">
                          {displayNPRCurrency(item.totalAmount)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="tag text-neutral-400 mb-0.5">
                        {ORDER_ID_LABEL}
                      </p>
                      <p className="text-[10px] md:text-xs font-mono font-bold text-primary">
                        #
                        {item?.paymentDetails?.paymentId
                          ?.slice(-8)
                          .toUpperCase()}
                      </p>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="p-4 md:p-6">
                    <div className="space-y-4">
                      {item?.productDetails.map((product, pIdx) => (
                        <div
                          key={product.productId + pIdx}
                          className="flex items-center gap-3 md:gap-5"
                        >
                          <div className="w-14 h-14 md:w-18 md:h-18 bg-surface-muted rounded-xl p-2 flex-shrink-0 border border-surface-border">
                            <img
                              src={product.image[0]}
                              className="w-full h-full object-contain mix-blend-multiply"
                              alt={product.name}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-bold text-primary line-clamp-1">
                              {product.name}
                            </h4>
                            <div className="flex items-center justify-between mt-1 gap-2">
                              <p className="text-xs text-neutral-400 font-medium">
                                {ORDER_QTY_LABEL}: {product.quantity}
                              </p>
                              <button
                                onClick={() =>
                                  handleReturnClick(item._id, product)
                                }
                                className="text-[10px] font-black text-accent uppercase tracking-widest hover:underline flex-shrink-0"
                              >
                                {ORDER_RETURN_ITEM}
                              </button>
                            </div>
                          </div>
                          <p className="text-sm font-black text-primary flex-shrink-0 hidden sm:block">
                            {displayNPRCurrency(product.price)}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Footer */}
                    <div className="mt-5 pt-4 border-t border-surface-border flex items-center justify-between">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${isPaid ? "bg-green-50 text-green-600" : "bg-yellow-50 text-yellow-600"}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${isPaid ? "bg-green-500 animate-pulse" : "bg-yellow-500"}`}
                        />
                        {isPaid ? ORDER_STATUS_PAID : ORDER_STATUS_PROCESSING}
                      </span>
                      <button
                        onClick={() => handleDetailsClick(item)}
                        className="flex items-center gap-1.5 text-[11px] font-black text-primary uppercase tracking-widest hover:text-accent transition-colors group"
                      >
                        {ORDER_DETAILS_LINK}
                        <FaChevronRight className="text-[8px] group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <RefundRequestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        orderId={selectedOrder}
        product={selectedProduct}
      />

      <OrderDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        order={currentOrderDetails}
      />
    </div>
  );
};

export default OrderPage;
